"use client";

import styled from "@emotion/styled";
import { Divider, Modal } from "antd";
import { Fragment, useEffect, useRef } from "react";
import { OCTOLANE_COLOR_TOKENS } from "~/app/styles/tokens";

import If from "~/core/ui/If";

import DeleteConfirmModal from "../DeleteConfirmModal";
import { COMMANDBAR_WIDTH } from "../GlobalCommandbar/constants/common";

import Footer from "./components/Footer";
import { OpportunityForm } from "./components/Form";
import CreateOpportunityHeader from "./components/Header";
import GlobalNewOpportunityContextProvider from "./context/Provider";
import { useGlobalNewOpportunityModal } from "./hooks/useGlobalNewOpportunityModal";
import { useGlobalCreateOpportunityStore } from "./store";

const StyledModal = styled(Modal)`
  .ant-modal-content {
    padding: 0 !important;
    border: 1px solid ${OCTOLANE_COLOR_TOKENS.border.light};
  }
`;

export default function GlobalNewOpportunityModal() {
  const formContainerRef = useRef<HTMLDivElement>(null);
  const isOpenGlobalCreateOpportunity = useGlobalCreateOpportunityStore(
    state => state.isOpenGlobalCreateOpportunity,
  );

  const setIsConfirmCloseForm = useGlobalCreateOpportunityStore(
    state => state.setIsConfirmCloseForm,
  );
  const isConfirmCloseForm = useGlobalCreateOpportunityStore(
    state => state.isConfirmCloseForm,
  );

  const { handleCancel, handleClose, form } = useGlobalNewOpportunityModal();

  const isAdditionalFieldsOpen = useGlobalCreateOpportunityStore(
    state => state.isAdditionalFieldsOpen,
  );

  useEffect(() => {
    if (isAdditionalFieldsOpen && formContainerRef.current) {
      const container = formContainerRef.current;
      const scrollHeight = container.scrollHeight;
      const containerHeight = container.clientHeight;

      // Calculate the quarter point
      const scrollToMiddle = (scrollHeight - containerHeight) / 4;

      container.scrollTo({
        top: scrollToMiddle,
        behavior: "smooth",
      });
    }
  }, [isAdditionalFieldsOpen]);

  const onCloseConfirmModal = () => {
    setIsConfirmCloseForm(false);
  };

  return (
    <Fragment>
      <StyledModal
        centered
        footer={null}
        closeIcon={null}
        open={isOpenGlobalCreateOpportunity}
        onCancel={handleCancel}
        width={COMMANDBAR_WIDTH}
        onClose={handleCancel}
      >
        <GlobalNewOpportunityContextProvider form={form}>
          <CreateOpportunityHeader />
          <Divider className="!my-0" />
          <div
            className="space-y-2 max-h-[373px] overflow-y-auto"
            ref={formContainerRef}
          >
            <OpportunityForm />
          </div>
          <Divider className="!my-0" />
          <Footer />
        </GlobalNewOpportunityContextProvider>
      </StyledModal>

      <If condition={isConfirmCloseForm}>
        <DeleteConfirmModal
          isOpen={isConfirmCloseForm}
          onDismiss={onCloseConfirmModal}
          heading="Unsaved opportunity data will be lost"
          deleteButtonLabel="Discard changes"
          cancelButtonLabel="Continue editing"
          showLeftActionButton
          leftActionButtonLabel="Cancel"
          onDelete={handleClose}
          onCancel={onCloseConfirmModal}
          leftActionButtonOnClick={onCloseConfirmModal}
        >
          <div className="space-y-2">
            <p className="text-light">
              Would you like to continue editing or discard your changes?
            </p>
          </div>
        </DeleteConfirmModal>
      </If>
    </Fragment>
  );
}
